import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Gift, X, RefreshCw, Search, Eye, Send, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_PILL = {
  active: 'bg-[#06D6A0]/10 text-[#06D6A0]',
  pending_payment: 'bg-[#FB923C]/10 text-[#FB923C]',
  cancelled: 'bg-[#FF3B6B]/10 text-[#FF3B6B]',
  default: 'bg-[#E8E5DF] text-[#6B6B7A]',
};

export const CrmGiftCards = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [previewCard, setPreviewCard] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/gift-cards`, { withCredentials: true });
      setCards(r.data || []);
    } catch (e) {
      toast.error('Failed to load gift cards');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const filtered = useMemo(() => {
    let rows = cards;
    if (statusFilter !== 'all') rows = rows.filter((c) => c.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((c) =>
        c.code?.toLowerCase().includes(q) ||
        c.recipient_name?.toLowerCase().includes(q) ||
        c.recipient_email?.toLowerCase().includes(q) ||
        c.purchaser_name?.toLowerCase().includes(q) ||
        c.purchaser_email?.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [cards, statusFilter, search]);

  const stats = useMemo(() => {
    const active = cards.filter((c) => c.status === 'active');
    return {
      total_issued: active.length,
      total_value: active.reduce((s, c) => s + (c.amount || 0), 0),
      total_outstanding: active.reduce((s, c) => s + (c.balance_remaining || 0), 0),
      pending: cards.filter((c) => c.status === 'pending_payment').length,
    };
  }, [cards]);

  const cancel = async (gift_card_id, code) => {
    if (!window.confirm(`Cancel gift card ${code}? This prevents future redemptions but won't refund the purchaser.`)) return;
    try {
      await axios.post(`${API_URL}/api/admin/gift-cards/${gift_card_id}/cancel`, {}, { withCredentials: true });
      toast.success(`Gift card ${code} cancelled`);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="crm-gift-cards">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Gift Cards</h2>
          <p className="text-sm text-[#6B6B7A]">Issued vouchers and their remaining balance.</p>
        </div>
        <Button variant="outline" onClick={fetch} className="rounded-full" data-testid="gc-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Active cards" value={stats.total_issued} testid="gc-stat-active" />
        <StatCard label="Total face value" value={`$${stats.total_value.toFixed(2)}`} testid="gc-stat-value" />
        <StatCard label="Outstanding balance" value={`$${stats.total_outstanding.toFixed(2)}`} testid="gc-stat-outstanding" />
        <StatCard label="Pending payment" value={stats.pending} testid="gc-stat-pending" />
      </div>

      <div className="bg-white border border-[#E8E5DF] rounded-2xl p-4">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B7A]" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Code, recipient, purchaser…"
              className="pl-10" data-testid="gc-search"
            />
          </div>
          <div className="flex items-center gap-2">
            {['all', 'active', 'pending_payment', 'cancelled'].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full ${statusFilter === s ? 'bg-[#FF3B6B] text-white' : 'bg-[#E8E5DF]/50 text-[#6B6B7A]'}`}
                data-testid={`gc-filter-${s}`}
              >
                {s === 'all' ? 'All' : s === 'pending_payment' ? 'Pending' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="text-center py-10 text-[#6B6B7A]">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12" data-testid="gc-empty">
            <Gift className="w-10 h-10 mx-auto text-[#FF3B6B]/30 mb-2" />
            <p className="text-sm text-[#6B6B7A]">No gift cards match these filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[#6B6B7A] border-b border-[#E8E5DF]">
                  <th className="py-2">Code</th>
                  <th className="py-2">Amount</th>
                  <th className="py-2">Balance</th>
                  <th className="py-2">Recipient</th>
                  <th className="py-2">From</th>
                  <th className="py-2">Purchased</th>
                  <th className="py-2">Status</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.gift_card_id}
                    className="border-b border-[#E8E5DF]/50 hover:bg-[#FFFDF8] cursor-pointer"
                    onClick={() => setPreviewCard(c)}
                    data-testid={`gc-admin-row-${c.code}`}
                  >
                    <td className="py-2.5 font-mono font-bold text-[#1E1E24]">{c.code}</td>
                    <td className="py-2.5 text-[#1E1E24]">${(c.amount || 0).toFixed(2)}</td>
                    <td className="py-2.5 font-semibold text-[#FF3B6B]">${(c.balance_remaining || 0).toFixed(2)}</td>
                    <td className="py-2.5 text-[#1E1E24]">
                      <div className="leading-tight">
                        <p>{c.recipient_name}</p>
                        <p className="text-xs text-[#6B6B7A]">{c.recipient_email}</p>
                      </div>
                    </td>
                    <td className="py-2.5 text-[#6B6B7A] text-xs">
                      <p>{c.purchaser_name || '—'}</p>
                      <p>{c.purchaser_email}</p>
                    </td>
                    <td className="py-2.5 text-xs text-[#6B6B7A]">
                      {c.purchased_at ? new Date(c.purchased_at).toLocaleDateString() : '—'}
                      {c.payment_method && <span className="block text-[10px]">{c.payment_method}</span>}
                    </td>
                    <td className="py-2.5"><span className={`text-[10px] font-bold px-2 py-1 rounded-full uppercase ${STATUS_PILL[c.status] || STATUS_PILL.default}`}>{c.status}</span></td>
                    <td className="py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => setPreviewCard(c)}
                          className="text-[#6B6B7A] hover:bg-[#6B6B7A]/10"
                          data-testid={`gc-view-${c.code}`}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" /> View
                        </Button>
                        {c.status === 'active' && (
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => cancel(c.gift_card_id, c.code)}
                            className="text-[#FF3B6B] hover:bg-[#FF3B6B]/10"
                            data-testid={`gc-cancel-${c.code}`}
                          >
                            <X className="w-3.5 h-3.5 mr-1" /> Cancel
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {previewCard && (
        <GiftCardPreviewDialog
          card={previewCard}
          onClose={() => setPreviewCard(null)}
          onResent={() => { fetch(); }}
          onCancelled={() => { setPreviewCard(null); fetch(); }}
        />
      )}
    </div>
  );
};

// ─────────────── Preview + Resend modal ───────────────
const GiftCardPreviewDialog = ({ card, onClose, onResent, onCancelled }) => {
  const [pdfUrl, setPdfUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const [overrideEmail, setOverrideEmail] = useState('');
  const [showOverride, setShowOverride] = useState(false);

  useEffect(() => {
    let revokeUrl = null;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/admin/gift-cards/${card.gift_card_id}/pdf`, {
          withCredentials: true, responseType: 'blob',
        });
        revokeUrl = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
        setPdfUrl(revokeUrl);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load PDF preview');
      } finally { setLoading(false); }
    })();
    return () => { if (revokeUrl) window.URL.revokeObjectURL(revokeUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.gift_card_id]);

  const downloadPdf = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl; a.download = `creative-birthdays-gift-${card.code}.pdf`;
    a.click();
  };

  const resend = async () => {
    if (card.status !== 'active') {
      toast.error('Only active gift cards can be resent.');
      return;
    }
    setResending(true);
    try {
      const payload = overrideEmail.trim() ? { recipient_email: overrideEmail.trim() } : {};
      const r = await axios.post(`${API_URL}/api/admin/gift-cards/${card.gift_card_id}/resend`,
        payload, { withCredentials: true });
      toast.success(`Gift card resent to ${r.data.recipient_email}`);
      setOverrideEmail('');
      setShowOverride(false);
      onResent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resend failed');
    } finally { setResending(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="gc-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-[#FF3B6B]" />
            Gift Card <span className="font-mono text-[#FF3B6B]">{card.code}</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${STATUS_PILL[card.status] || STATUS_PILL.default}`}>{card.status}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-3 gap-4">
          {/* PDF preview */}
          <div className="md:col-span-2 bg-[#FFFDF8] border border-[#E8E5DF] rounded-2xl overflow-hidden min-h-[500px] flex items-center justify-center">
            {loading ? (
              <div className="text-center text-[#6B6B7A]" data-testid="gc-preview-loading">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-[#FF3B6B]" />
                <p className="text-xs">Loading voucher…</p>
              </div>
            ) : pdfUrl ? (
              <iframe
                src={pdfUrl}
                title={`Gift card ${card.code} voucher`}
                className="w-full h-[600px] border-0"
                data-testid="gc-preview-iframe"
              />
            ) : (
              <p className="text-xs text-[#FF3B6B]">Preview unavailable</p>
            )}
          </div>

          {/* Details + actions */}
          <div className="space-y-4">
            <div className="bg-white border border-[#E8E5DF] rounded-xl p-4 space-y-2 text-sm">
              <DetailRow label="Face value" value={`$${(card.amount || 0).toFixed(2)}`} />
              <DetailRow label="Balance" value={<span className="text-[#FF3B6B] font-bold">${(card.balance_remaining || 0).toFixed(2)}</span>} />
              <DetailRow label="To" value={card.recipient_name} />
              <DetailRow label="Email" value={<span className="font-mono text-[11px] break-all">{card.recipient_email}</span>} />
              <DetailRow label="From" value={card.purchaser_name || '—'} />
              <DetailRow label="Purchased" value={card.purchased_at ? new Date(card.purchased_at).toLocaleDateString() : '—'} />
              <DetailRow label="Expires" value={card.expires_at ? new Date(card.expires_at).toLocaleDateString() : '—'} />
              <DetailRow label="Payment" value={card.payment_method || '—'} />
              {card.last_resent_at && (
                <DetailRow label="Last resent" value={<span className="text-[10px] text-[#6B6B7A]">{new Date(card.last_resent_at).toLocaleString()}</span>} />
              )}
            </div>

            {/* iter-143 — QR code (encodes the same redeem URL embedded in the PDF) */}
            <div className="bg-white border border-[#E8E5DF] rounded-xl p-4 text-center" data-testid="gc-preview-qr-card">
              <p className="text-[10px] text-[#6B6B7A] uppercase tracking-widest mb-2">Scan to redeem</p>
              <img
                src={`${API_URL}/api/admin/gift-cards/${card.gift_card_id}/qr.png`}
                alt={`QR code for gift card ${card.code}`}
                className="mx-auto rounded-lg border border-[#E8E5DF]"
                style={{ width: 160, height: 160 }}
                data-testid="gc-preview-qr-img"
              />
              <p className="text-[10px] text-[#6B6B7A] mt-2 font-mono break-all" data-testid="gc-preview-qr-code">
                {card.code}
              </p>
              <p className="text-[10px] text-[#6B6B7A]">Opens the booking flow with the code applied</p>
            </div>

            {card.message && (
              <div className="bg-[#FFFDF8] border-l-3 border-[#FF3B6B] p-3 rounded-r-lg" data-testid="gc-preview-message">
                <p className="text-[10px] text-[#6B6B7A] uppercase tracking-widest mb-1">Personal message</p>
                <p className="text-xs italic text-[#1E1E24]">&ldquo;{card.message}&rdquo;</p>
              </div>
            )}

            {(card.redemptions || []).length > 0 && (
              <div className="bg-white border border-[#E8E5DF] rounded-xl p-3" data-testid="gc-preview-redemptions">
                <p className="text-[10px] text-[#6B6B7A] uppercase tracking-widest mb-2">Redemption history</p>
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {card.redemptions.map((r, i) => (
                    <div key={`${r.booking_id}-${i}`} className="flex items-center justify-between text-xs">
                      <span className="font-mono text-[10px] text-[#6B6B7A]">{r.booking_reference || r.booking_id?.slice(-8)}</span>
                      <span className={`font-semibold ${r.amount < 0 ? 'text-[#06D6A0]' : 'text-[#FF3B6B]'}`}>{r.amount > 0 ? '−' : '+'}${Math.abs(r.amount).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Resend section */}
            {card.status === 'active' && (
              <div className="bg-white border border-[#E8E5DF] rounded-xl p-4">
                <p className="text-xs font-semibold text-[#1E1E24] mb-2">Resend voucher</p>
                {showOverride ? (
                  <div className="space-y-2 mb-2">
                    <Label className="text-[10px] text-[#6B6B7A]">Send to a different email</Label>
                    <Input
                      type="email"
                      value={overrideEmail}
                      onChange={(e) => setOverrideEmail(e.target.value)}
                      placeholder={card.recipient_email}
                      data-testid="gc-resend-override-email"
                      className="h-8 text-xs"
                    />
                    <button
                      type="button"
                      onClick={() => { setShowOverride(false); setOverrideEmail(''); }}
                      className="text-[10px] text-[#6B6B7A] hover:text-[#FF3B6B] underline"
                      data-testid="gc-resend-override-cancel"
                    >
                      Send to original instead
                    </button>
                  </div>
                ) : (
                  <p className="text-[11px] text-[#6B6B7A] mb-2">
                    To <b>{card.recipient_email}</b> · <button type="button" onClick={() => setShowOverride(true)} className="text-[#FF3B6B] hover:underline" data-testid="gc-resend-change-email">Change email</button>
                  </p>
                )}
                <Button
                  onClick={resend}
                  disabled={resending}
                  className="w-full bg-[#06D6A0] hover:bg-[#05B88A] text-white text-xs"
                  data-testid="gc-resend-btn"
                >
                  <Send className="w-3.5 h-3.5 mr-1" />
                  {resending ? 'Sending…' : 'Resend email'}
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={downloadPdf} disabled={!pdfUrl} data-testid="gc-preview-download">
            <Download className="w-3.5 h-3.5 mr-1" /> Download PDF
          </Button>
          <Button variant="outline" onClick={onClose} data-testid="gc-preview-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-2 text-xs">
    <span className="text-[#6B6B7A]">{label}</span>
    <span className="text-[#1E1E24] font-medium text-right">{value}</span>
  </div>
);

const StatCard = ({ label, value, testid }) => (
  <div className="bg-white border border-[#E8E5DF] rounded-2xl p-4" data-testid={testid}>
    <p className="text-xs text-[#6B6B7A] uppercase tracking-widest">{label}</p>
    <p className="text-2xl font-bold text-[#1E1E24] mt-1">{value}</p>
  </div>
);

export default CrmGiftCards;
